import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { BY_SYMBOL, elementColor } from '../elements';
import { molarMass, parseFormula } from '../formula';
import { subscriptFormula } from './equation';
import {
  DARK_INK,
  NowYouTry,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_FOCUS,
  cueHighlights,
  cueIsActive,
  usePrefersReducedMotion,
  useMountSequence,
  type VizCue,
} from '../vizShared';

// MoleConverter (type 'mole_converter') — the "mole map": interconvert mass ⇄
// moles ⇄ particles (Avogadro) ⇄ gas volume (22.4 L at STP) for a chosen
// formula. Edit any field; the others update live, and the active conversion
// factors are shown with units. config: { formula?, title?, examples? }.
// Resilient: unparseable formula falls back to a default.

export interface MoleConverterConfig {
  title?: string;
  formula?: string;
  examples?: boolean;
}

interface Props {
  config?: MoleConverterConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const NA = 6.022e23; // Avogadro's number (particles / mol)
const MOLAR_VOLUME = 22.4; // L / mol at STP
const FALLBACK = 'H2O';
const MAX_MOL = 5; // slider range upper bound (mol)

const EXAMPLE_FORMULAS = ['H2O', 'CO2', 'O2', 'NaCl', 'C6H12O6', 'NH3', 'CaCO3', 'H2SO4'];

type Field = 'mass' | 'moles' | 'particles' | 'volume';

export default function MoleConverter({ config = {}, onComplete, cue }: Props) {
  const showExamples = config.examples !== false;
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const [formula, setFormula] = useState(config.formula || FALLBACK);
  const interacted = useRef(false);

  const mm = useMemo(() => molarMass(formula), [formula]);
  const atoms = useMemo(() => parseFormula(formula), [formula]);
  const valid = mm > 0;

  // Canonical state is moles; everything derives from it.
  const [moles, setMoles] = useState(1);
  const [active, setActive] = useState<Field>('moles');

  useEffect(() => {
    setMoles(1);
    setActive('moles');
  }, [formula]);

  const fireComplete = () => {
    if (!interacted.current) {
      interacted.current = true;
      onComplete?.({ type: 'mole_converter', formula });
    }
  };

  const mass = moles * mm;
  const particles = moles * NA;
  const volume = moles * MOLAR_VOLUME;

  const setFrom = (field: Field, value: number) => {
    fireComplete();
    setActive(field);
    if (!Number.isFinite(value) || value < 0) value = 0;
    if (field === 'mass') setMoles(mm > 0 ? value / mm : 0);
    else if (field === 'moles') setMoles(value);
    else if (field === 'particles') setMoles(value / NA);
    else setMoles(value / MOLAR_VOLUME);
  };

  // ── Self-demo: sweep the amount up then settle at 1 mol, lighting the chain.
  const [demoSettled, setDemoSettled] = useState(false);
  const demo = useMountSequence(
    [
      { at: 380, run: () => setMoles(2) },
      { at: 760, run: () => setMoles(3.5) },
      { at: 1140, run: () => setMoles(2) },
    ],
    {
      skip: reduced || hasCue,
      onSettle: () => {
        setMoles(1);
        setActive('moles');
        setDemoSettled(true);
      },
    },
  );
  useEffect(() => {
    if (demo.done && !reduced && !hasCue) {
      setMoles(1);
      setDemoSettled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo.done]);

  const cancelDemo = () => {
    if (demo.running) demo.cancel();
  };

  // ── Cue handling: progress sets the slider position; highlight picks a
  // formula from the example set. ───────────────────────────────────────────
  useEffect(() => {
    if (cue?.progress != null) {
      const p = Math.max(0, Math.min(1, cue.progress));
      setMoles(Number((p * MAX_MOL).toFixed(3)));
      setActive('moles');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.progress]);
  useEffect(() => {
    const h = cueHighlights(cue);
    const match = EXAMPLE_FORMULAS.find((f) => h.includes(f.toLowerCase()));
    if (match) setFormula(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.highlight]);

  const fmt = (v: number) =>
    v === 0 ? '0' : v >= 1e6 || (v < 1e-3 && v > 0) ? v.toExponential(3) : Number(v.toPrecision(5)).toString();

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Stoichiometry · The mole map"
      title={config.title ?? 'Mole map converter'}
      subtitle="Mass ⇄ moles ⇄ particles ⇄ gas volume — everything routes through the mole."
      hint="Drag the slider or type into any field · ←→ adjusts the slider when focused."
      actions={
        <div className="flex items-center gap-1.5">
          {showExamples && (
            <select
              value={EXAMPLE_FORMULAS.includes(formula) ? formula : ''}
              onChange={(e) => {
                cancelDemo();
                if (e.target.value) setFormula(e.target.value);
              }}
              aria-label="Choose an example formula"
              className={`rounded-sm border border-ws-border bg-ws-tile px-2 py-1 text-xs text-ws-ink-2 outline-none transition-colors hover:bg-ws-surface-2 focus:border-ws-accent ${VIZ_FOCUS}`}
            >
              <option value="">Examples…</option>
              {EXAMPLE_FORMULAS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => {
              cancelDemo();
              setFrom('moles', 1);
            }}
            className={VIZ_BTN_GHOST}
            aria-label="Reset to 1 mole"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      }
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={demoSettled && !hasCue} />

        {/* Formula + molar mass breakdown */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-ws-border bg-ws-surface-2 px-3 py-2">
          <span className="font-mono text-lg font-bold text-ws-ink">{subscriptFormula(formula)}</span>
          {valid ? (
            <>
              <span className="text-xs text-ws-ink-3">
                M = <span className="font-semibold text-ws-ink">{mm.toFixed(2)} g/mol</span>
              </span>
              <span className="flex flex-wrap items-center gap-1 text-[11px] text-ws-ink-4">
                {atoms.map((a, i) => {
                  const el = BY_SYMBOL[a.symbol];
                  return (
                    <span key={a.symbol} className="flex items-center gap-0.5">
                      {i > 0 && <span className="text-ws-ink-4">+</span>}
                      <span
                        className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold"
                        style={{ backgroundColor: elementColor(a.symbol), color: DARK_INK }}
                      >
                        {a.symbol}
                      </span>
                      {a.count}×{el ? el.mass.toFixed(2) : '?'}
                    </span>
                  );
                })}
              </span>
            </>
          ) : (
            <span className="text-xs text-ws-danger">Could not parse formula.</span>
          )}
        </div>

        {/* Continuous amount slider (native, keyboard + touch) */}
        <div className="rounded-md border border-ws-border bg-ws-surface p-3">
          <div className="mb-1.5 flex items-center justify-between text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
            <span>Amount</span>
            <span className="font-mono text-xs normal-case tracking-normal text-ws-ink">
              {fmt(moles)} mol
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={MAX_MOL}
            step={0.05}
            value={Math.max(0, Math.min(MAX_MOL, moles))}
            onChange={(e) => {
              cancelDemo();
              setFrom('moles', Number(e.target.value));
            }}
            aria-label="Amount in moles"
            className={`h-2 w-full cursor-pointer appearance-none rounded-full bg-ws-surface-2 accent-ws-accent ${VIZ_FOCUS}`}
          />
          <div className="mt-1 flex justify-between text-[9px] text-ws-ink-4">
            <span>0 mol</span>
            <span>{MAX_MOL} mol</span>
          </div>
        </div>

        {/* The four interchangeable quantities */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <ConvCard
            label="Mass"
            unit="g"
            value={mass}
            active={active === 'mass'}
            onChange={(v) => {
              cancelDemo();
              setFrom('mass', v);
            }}
            fmt={fmt}
            accent="#E0564B"
          />
          <ConvCard
            label="Amount"
            unit="mol"
            value={moles}
            active={active === 'moles'}
            onChange={(v) => {
              cancelDemo();
              setFrom('moles', v);
            }}
            fmt={fmt}
            accent="#3B82C4"
          />
          <ConvCard
            label="Particles"
            unit="molecules"
            value={particles}
            active={active === 'particles'}
            onChange={(v) => {
              cancelDemo();
              setFrom('particles', v);
            }}
            fmt={fmt}
            accent="#A855C7"
          />
          <ConvCard
            label="Gas volume (STP)"
            unit="L"
            value={volume}
            active={active === 'volume'}
            onChange={(v) => {
              cancelDemo();
              setFrom('volume', v);
            }}
            fmt={fmt}
            accent="#5FB860"
          />
        </div>

        {/* The conversion-factor chain centred on moles */}
        <div className="rounded-md border border-ws-border bg-ws-surface p-3 text-[11px] text-ws-ink-2">
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-ws-ink-3">
            Conversion factors (everything routes through moles)
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Factor a="mass (g)" b="mol" expr={`÷ ${mm.toFixed(2)} g/mol`} lit={active === 'mass'} />
            <Factor a="mol" b="particles" expr={`× 6.022×10²³ /mol`} lit={active === 'particles'} />
            <Factor a="mol" b="volume (L)" expr={`× 22.4 L/mol`} lit={active === 'volume'} />
          </div>
        </div>
      </div>
    </VizCard>
  );
}

function ConvCard({
  label,
  unit,
  value,
  active,
  onChange,
  fmt,
  accent,
}: {
  label: string;
  unit: string;
  value: number;
  active: boolean;
  onChange: (v: number) => void;
  fmt: (v: number) => string;
  accent: string;
}) {
  // Local text state so users can type freely (incl. exponentials) per field.
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const display = editing ? text : fmt(value);

  return (
    <label
      className={`flex flex-col gap-1 rounded-md border px-3 py-2 transition-all duration-300 ${
        active ? 'border-ws-accent bg-ws-accent-quiet/40 shadow-sm' : 'border-ws-border bg-ws-tile'
      }`}
    >
      <span className="flex items-center gap-1.5 text-[11px] font-medium text-ws-ink-3">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accent }} />
        {label}
      </span>
      <span className="flex items-baseline gap-1">
        <input
          type="text"
          inputMode="decimal"
          value={display}
          aria-label={`${label} in ${unit}`}
          onFocus={(e) => {
            setEditing(true);
            setText(fmt(value));
            e.target.select();
          }}
          onBlur={() => setEditing(false)}
          onChange={(e) => {
            setText(e.target.value);
            const v = Number(e.target.value);
            if (Number.isFinite(v)) onChange(v);
          }}
          className={`w-full min-w-0 rounded-sm bg-transparent font-mono text-base font-bold text-ws-ink outline-none ${VIZ_FOCUS}`}
        />
        <span className="shrink-0 text-xs text-ws-ink-4">{unit}</span>
      </span>
    </label>
  );
}

function Factor({ a, b, expr, lit }: { a: string; b: string; expr: string; lit?: boolean }) {
  return (
    <span
      className={`flex items-center gap-1 rounded-sm px-2 py-1 transition-colors duration-300 ${
        lit ? 'bg-ws-accent-quiet/60 ring-1 ring-ws-accent/50' : 'bg-ws-surface-2'
      }`}
    >
      <span className="text-ws-ink-3">{a}</span>
      <ArrowRight size={11} className="text-ws-ink-4" />
      <span className="text-ws-ink-3">{b}</span>
      <span className="font-mono text-ws-accent">{expr}</span>
    </span>
  );
}
